import { createSelector, createSlice } from "@reduxjs/toolkit"
import { logout } from "../common/reducers/initContext";
import { dataHookFactory, initMapFactory, DefaultFailureAction, DefaultSuccessAction, dataHookWithCredentialsFactory } from "../common/reducers/utilities"
import { ensureArray, groupBy } from "../common/utils/arrayUtilities";
import { markFetched, registerEntityBasedFetch } from "../common/hooks/initHook";

const key = o => o.id;

export const locationOrientationSlice = createSlice({
    name: 'locationOrientations',
    initialState: {
        data: {}
    },
    reducers: {
        setForLocation: (state, action) => {
            const locationids = new Set(ensureArray(action.payload.ids));
            const toRemove = Object.values(state.data).filter(o => locationids.has(o.locationid));
            toRemove.forEach(o => delete state.data[key(o)]);
            ensureArray(action.payload.data).forEach(o => state.data[key(o)] = o);
        },
        setForMember: (state, action) => {
            const memberids = new Set(ensureArray(action.payload.ids));
            const toRemove = Object.values(state.data).filter(o => memberids.has(o.memberid));
            toRemove.forEach(o => delete state.data[key(o)]);
            ensureArray(action.payload.data).forEach(o => state.data[key(o)] = o);
        },
        addOrUpdate: (state, action) => {
            ensureArray(action.payload).forEach(o => state.data[key(o)] = o);
        },
        remove: (state, action) => {
            ensureArray(action.payload).forEach(o => delete state.data[key(o)]);
        }
    },
    extraReducers: builder => {
        builder.addCase(logout, () => locationOrientationSlice.getInitialState());
    }
});

// #region network operations
const visualizeSuccess = (verb, action) => DefaultSuccessAction(verb, 'locationOrientations', action);
const visualizeFailure = verb => DefaultFailureAction(verb, 'locationOrientations');

const fetchLocationOrientations = locationid => network => network.get('/api/v1/locationOrientations/' + encodeURIComponent(locationid), data => locationOrientationSlice.actions.setForLocation({ ids: [locationid], data }), visualizeFailure('get'));
const fetchLocationOrientationsForMember = memberid => network => network.get('/api/v1/locationMemberOrientations/' + encodeURIComponent(memberid), data => locationOrientationSlice.actions.setForMember({ ids: [memberid], data }), visualizeFailure('get'));

export const createLocationOrientation = (network, orientation) => network.post('/api/v1/locationOrientation', orientation, locationOrientationSlice.actions.addOrUpdate, visualizeFailure('create'));
export const updateLocationOrientation = (network, delta) => network.put('/api/v1/locationOrientation', delta, locationOrientationSlice.actions.addOrUpdate, visualizeFailure('update'));
export const removeLocationOrientaiton = (network, id) => network.delete('/api/v1/locationOrientation/' + encodeURIComponent(id), locationOrientationSlice.actions.remove(id), visualizeFailure('remove'))
// #engregion

// #region selectors
const selectLocationOrientationData = state => state.locationOrientations.data;
const selectLocationOrientationLocationData = createSelector([selectLocationOrientationData], data => groupBy(Object.values(data), 'locationid'));
const selectLocationOrientationMemberData = createSelector([selectLocationOrientationData], data => groupBy(Object.values(data), 'memberid'));
const selectAllLocationOrientations = createSelector([selectLocationOrientationData], orientations => Object.values(orientations));
const selectLocationOrientations = locationid => createSelector([selectLocationOrientationLocationData], (orientations) => orientations[locationid] ?? []);
const selectMemberLocationOrientations = memberid => createSelector([selectLocationOrientationMemberData], (orientations) => orientations[memberid] ?? []);
// #endregion

const listen = {
    locationOrientationCreated: (orientation, { dispatch, websocket }) => {
        markFetched(websocket, 'location_orientation', key(orientation))
        dispatch(locationOrientationSlice.actions.addOrUpdate(orientation))
    },
    locationOrientationUpdated: (orientation, { dispatch }) => dispatch(locationOrientationSlice.actions.addOrUpdate(orientation)),
    locationOrientationRemoved: (orientation, { dispatch }) => dispatch(locationOrientationSlice.actions.remove(orientation)),
}
const locationOrientationInit = initMapFactory(fetchLocationOrientations, listen);
registerEntityBasedFetch('location', locationOrientationInit)

const memberLocationOrientationInit = initMapFactory(fetchLocationOrientationsForMember, listen);
registerEntityBasedFetch('member', memberLocationOrientationInit);

const cleanup = { root: locationOrientationSlice.name, all: selectAllLocationOrientations, remove: locationOrientationSlice.actions.remove, key };

const options = {
    cleanup: {  root: locationOrientationSlice.name, all: selectAllLocationOrientations, remove: locationOrientationSlice.actions.remove, key},
    condition: credentials => {
        return canViewLocationOrientations(credentials)
    }
}
const singleOptions = { ...options, defaultValue: null };

const fullRoles = ['administrator', 'event manager', 'template manager'];
const viewRoles = ['administrator', 'event manager', 'template manager', 'auditor', 'event auditor', 'template auditor'];
export const canUpdateLocationsOrientations = (credentials, member) => checkPermissions(credentials, member, fullRoles, !member.inactive);
export const canCreateOrientations = canUpdateLocationsOrientations;
export const canRemoveOrientations = canUpdateLocationsOrientations;
export const hideLocationsOrientations = (credentials, member) => !checkPermissions(credentials, member, viewRoles);
export const canViewLocationOrientations = (credentials, member) => checkPermissions(credentials, member, viewRoles);

export const [useLocationOrientations] = dataHookFactory(selectLocationOrientations, locationOrientationInit, {cleanup});
export const [useLocationOrientationsForMember] = dataHookWithCredentialsFactory(selectMemberLocationOrientations, memberLocationOrientationInit, singleOptions);

const checkPermissions = (credentials, member, requiredRoles, condition) => {
    const access = credentials.hasRole(requiredRoles, {office: member?.office, client: member?.default_client, location: member?.default_location});
    return (access && condition !== false && !member?.inactive) ?? false 
}

const reducer = { name: locationOrientationSlice.name, reducer: locationOrientationSlice.reducer }
export default reducer;